import { Box, Button, Container, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { MaterialReactTable, MRT_TableInstance } from "material-react-table";
import { columns } from "./columns";
import { useContext, useEffect, useMemo, useState } from "react";
import { RecordingLedgersContext } from "./RecordingLedgersContext";
import Actions from "./Actions";
import { UserContext } from "../../contexts/UserContext";
import { StringParam, useQueryParam } from "use-query-params";
import { InternalUser } from "../../types/app";
import axios from "axios";
import NotesModal from "../../checks/checkNotes/NotesModal";
import { RecordingLedger } from "./types";
import { NotesColumn } from "../NotesColumn";
import Iconify from "../../../minimals/components/iconify";
import { exportTableToCsv } from "../../taskUtils";
import ClosingChecklistEscrow from "../../taskManagement/ClosingChecklistEscrow";
import { saveEscrowLedgerNote } from "./saveNote";
import dayjs, { Dayjs } from "dayjs";
import { saveLedgerAssignTo } from "../shared/saveLedgerAssignTo";
import { DashboardReportType } from "../../types/reports";
import ReportBubbles from "../../orders/ReportBubbles";

const today = dayjs();
const defaultEndDate = today.add(5, "day");
const defaultStartDate = null;

export default function RecordingLedgers() {
  const {
    recordingLedgers,
    loading,
    assignedToMe,
    setAssignedToMe,
    getRecordingLedgers,
    setRecordingLedgers,
    tableRef,
    checklist23Completed,
    setChecklist23Completed,
    recordClosingDocsReceived,
    setRecordClosingDocsReceived
  } = useContext(RecordingLedgersContext);
  const { user } = useContext(UserContext);
  const [assignedToId] = useQueryParam("assignedtoid", StringParam);
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<RecordingLedger | null>(null);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [openClosingChecklistEscrow, setOpenClosingChecklistEscrow] = useState<boolean>(false);
  const [showZeroAmount, setShowZeroAmount] = useState<boolean>(false);
  const [tableFilters, setTableFilters] = useState<any[]>([]);
  const [assignableInternalUserIds, setAssignableInternalUserIds] = useState<number[]>([]);

  const isUnassignedOnly = Number(assignedToId) == -1;

  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };

  const getAndSetAssignableInternalUserIds = async () => {
    const id = assignedToId && !isUnassignedOnly ? Number(assignedToId) : user?.id;
    if (!id) {
      setAssignableInternalUserIds([]);
      return;
    }

    const { data } = await axios.get<number[]>(
      `/api/clientphonebook/internalusers/getInternalUserIdsIncludingPairedUsers?internalUserIds=${id}`
    );
    setAssignableInternalUserIds(data);
  };

  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };

  const filteredEscrowLedgers = useMemo(() => {
    let ledgers = showZeroAmount ? recordingLedgers : recordingLedgers.filter((l) => l.amount);
    if (checklist23Completed) {
      ledgers = ledgers.filter((l) => l.satisfactionsCompleted);
    }
    if (recordClosingDocsReceived) {
      ledgers = ledgers.filter((l) => l.recordClosingDocsReceived);
    }

    if (isUnassignedOnly) {
      return ledgers.filter((o) => !o.assignedTo);
    }

    if (assignedToId) {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : [Number(assignedToId)];
      return ledgers.filter((o) => !!o.assignedToUser?.id && assignedToIds.includes(o.assignedToUser.id));
    }

    if (assignedToMe) {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : user?.id ? [user.id] : [];
      return ledgers.filter((o) => !!o.assignedToUser?.id && assignedToIds.includes(o.assignedToUser.id));
    }

    return ledgers;
  }, [
    recordingLedgers,
    assignedToMe,
    assignedToId,
    isUnassignedOnly,
    showZeroAmount,
    checklist23Completed,
    recordClosingDocsReceived,
    user?.id,
    assignableInternalUserIds
  ]);

  useEffect(() => {
    getAndSetAssignedToUser();
    getAndSetAssignableInternalUserIds();
  }, [assignedToId, user?.id]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };

  const getTotal = (table: MRT_TableInstance<RecordingLedger>) => {
    return loading ? 0 : table.getExpandedRowModel().rows.reduce((sum, row) => sum + (row.original?.amount || 0), 0);
  };

  const columnsWithNotes = NotesColumn<RecordingLedger>(columns, setSelectedOrder);

  const updateFilters = (newFilter: { id: string; value: any }) => {
    setTableFilters((prevFilters) => {
      const existingFilter = prevFilters.find((f) => f.id === newFilter.id);
      if (existingFilter) {
        return prevFilters.map((f) => (f.id === newFilter.id ? newFilter : f));
      } else {
        return [...prevFilters, newFilter];
      }
    });
  };

  useEffect(() => {
    if (checklist23Completed) {
      const defaultStatesFilter = { id: "orderInfo?.state", value: ["NJ", "PA", "IN", "FL"] };
      updateFilters(defaultStatesFilter);
    }
  }, [checklist23Completed]);

  useEffect(() => {
    if (recordClosingDocsReceived) {
      const excludedStates = ["FL", "NJ", "PA", "IN", "NY", "MTAUS"];
      const states = [
        ...new Set(
          recordingLedgers
            .filter((l) => l.orderInfo?.state && !excludedStates.includes(l.orderInfo.state))
            .map((l) => l.orderInfo?.state)
        )
      ];
      const defaultStatesFilter = { id: "orderInfo?.state", value: states };
      updateFilters(defaultStatesFilter);
    }
  }, [recordClosingDocsReceived]);

  return (
    <>
      <Helmet>
        <title>Recording Ledgers | TitleQ</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 2 }}>
          <ReportBubbles excludedPermissions={["PolicyIssues", "PoliciesToSend"]} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4" marginBottom={2}>
            Recording Ledgers
            {isUnassignedOnly && " - Unassigned"}
            {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
          </Typography>
        </Stack>
        <MaterialReactTable
          enableStickyHeader
          columns={columnsWithNotes}
          data={filteredEscrowLedgers}
          enableGlobalFilter
          enableFacetedValues
          globalFilterFn="includesString"
          enableColumnOrdering={enableOrdering}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 },
            showColumnFilters: true,
            columnVisibility: {
              dueDate: false,
              closer: false,
              funder: false
            }
          }}
          state={{
            showSkeletons: loading,
            columnFilters: tableFilters
          }}
          onColumnFiltersChange={setTableFilters}
          muiTableBodyProps={{
            sx: {
              "& .MuiTableRow-root:hover .MuiTableCell-root": {
                backgroundColor: "inherit !important"
              }
            }
          }}
          muiTableBodyRowProps={({ row }) => {
            // Apply zebra striping styles to alternate rows
            return {
              style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {} // Change the background color as needed
            };
          }}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <>
                <Box sx={{ flexGrow: 1, position: "relative" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Actions
                      assignedToMe={assignedToMe}
                      setAssignedToMe={setAssignedToMe}
                      count={loading ? null : table.getRowCount()}
                      total={getTotal(table)}
                      hideTotal={false}
                      hideAssignedToMeOptions={!!assignedToId}
                      hideReasignAction={true}
                      table={table}
                      fetchTableData={() => getRecordingLedgers(true)}
                      reportType={DashboardReportType.EscrowOverdue}
                      onRowsDeselected={onRowsDeselected}
                      setShowZeroAmount={setShowZeroAmount}
                      checklist23Completed={checklist23Completed}
                      setChecklist23Completed={setChecklist23Completed}
                      recordClosingDocsReceived={recordClosingDocsReceived}
                      setRecordClosingDocsReceived={setRecordClosingDocsReceived}
                    />
                    <Box sx={{ position: "absolute", right: 0 }}>
                      <Tooltip title="Column ordering">
                        <IconButton onClick={toggleColumnOrdering}>
                          <Iconify icon="mdi:reorder-vertical" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export to csv">
                        <IconButton onClick={() => exportTableToCsv(table, "export.csv")}>
                          <Iconify icon="eva:download-fill" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Box>
              </>
            );
          }}
        />
      </Container>
      {!!selectedOrder && (
        <NotesModal
          isOpen
          nameBeforeUpdate={selectedOrder?.assignedTo || ""}
          emailBeforeUpdate={
            selectedOrder?.assignedToEmail && selectedOrder.assignedToEmail.trim().toLowerCase() !== "null"
              ? selectedOrder.assignedToEmail.trim()
              : selectedOrder?.assignedToUser?.email || ""
          }
          entity={selectedOrder}
          setEntity={setSelectedOrder}
          setEntityTable={setRecordingLedgers}
          reportType={DashboardReportType.EscrowOverdue}
          saveLedgerAssignTo={async (assingedToUser) => {
            await saveLedgerAssignTo(selectedOrder.name, assingedToUser, user);
          }}
          saveLedgerNote={async (text) => {
            await saveEscrowLedgerNote(selectedOrder.name, text, user?.name as string);
          }}
        />
      )}
      {openClosingChecklistEscrow && (
        <ClosingChecklistEscrow
          onClose={() => setOpenClosingChecklistEscrow(false)}
          refresh={() => getRecordingLedgers(true)}
        />
      )}
    </>
  );
}
